import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { formatDate } from "simplydo/core/dates";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { styled } from "simplydo/react/styled";
import { mobileOnly, useTheme } from "simplydo/react/theme";
import {
  Button,
  Container,
  Icon,
  Label,
  Modal,
  Popup,
} from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { IdeaFollowButton } from "components/lib/FollowButton";
import { IdeaChallengeHelp, SubmitIdeaButton } from "components/lib/Ideas";
import { PlaceholderItem, Sticky } from "components/lib/UI";

import { useIdea } from "./hooks/useIdea";
import type { IdeaCollaboratorUser } from "./types";

const MobileContainer = styled.div`
  width: 100%;
  padding: 0 5px;
`;

const IdeaStatusBarDiv = styled.div`
  height: ${({ theme }) => (theme.sizes.isMobile ? 40 : 50)}px;
  padding: ${({ theme }) => (theme.sizes.isMobile ? "5px 5px" : "7.5px 15px")};
  background-image: ${({ theme }) =>
    `linear-gradient(to bottom right, ${theme.secondaryColour}, ${util.adjustColour(theme.secondaryColour, 0.5)})`};
  display: flex;
  align-items: center;
  top: 50px;
  width: 100%;
`;

const StatusLabel = styled(Label)`
  min-width: max-content;
  height: 27px;
  display: flex !important;
  align-items: center;
`;

const StatusbarInnerContainer = styled.div<{ $flexGrow: number }>`
  display: flex;
  align-items: center;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  flex-grow: ${({ $flexGrow }) => $flexGrow};
  min-width: ${({ $flexGrow }) => ($flexGrow === 0 ? "max-content" : 0)};
`;

const StatusbarContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  .submit-idea-button {
    ${mobileOnly} {
      height: 25px;
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 150px !important;
      > i {
        height: 1em !important;
      }
    }
  }
`;

const StyledButton = styled(Button)`
  margin-right: 0 !important;
  height: 30px;
  min-width: max-content;
`;

type IdeaStatusBarProps = {
  updateIdea: (data: unknown, path: string[], replace?: boolean) => void;
  isSaving: boolean;
  unsaved: boolean;
  isEditing: boolean;
  setEditing: (isEditing: boolean) => void;
  savedAt?: string | Date | "";
  onSubmit: (isSubmitted: boolean) => void;
  scrollToRequired?: (sectionId: string) => void;
  otherUsers: Record<string, IdeaCollaboratorUser>;
  websocketConnected: boolean;
};

const IdeaStatusBar = ({
  updateIdea,
  isSaving,
  unsaved,
  isEditing,
  setEditing,
  onSubmit,
  otherUsers = {},
  websocketConnected,
}: IdeaStatusBarProps) => {
  const [idea, ideaQuery] = useIdea();
  const loading = ideaQuery.isLoading;

  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const canShowHelpModal = useMemo(
    () => idea?.ownerChallenge?.stage === "published" && isEditing,
    [idea, isEditing],
  );

  const otherUsersValues = useMemo(
    () => Object.values(otherUsers) as IdeaCollaboratorUser[],
    [otherUsers],
  );
  const theme = useTheme();

  const StatusbarOuterContainer = useMemo(
    () => (theme.sizes.isMobile ? MobileContainer : Container),
    [theme],
  );

  return (
    <Sticky top={50} style={{ zIndex: 300 }}>
      <IdeaStatusBarDiv theme={theme}>
        {idea && canShowHelpModal ? (
          <Modal
            mountNode={document.getElementById("semantic-modal-mount-node")}
            open={helpModalOpen}
            onClose={() => setHelpModalOpen(false)}
            style={{ padding: 20, backgroundColor: "#fff" }}
          >
            <IdeaChallengeHelp
              challengeId={idea?.challenge}
              closeHelp={() => setHelpModalOpen(false)}
            />
          </Modal>
        ) : null}

        <StatusbarOuterContainer>
          <StatusbarContainer>
            <StatusbarInnerContainer $flexGrow={1}>
              {loading ? (
                <PlaceholderItem height={25} style={{ width: 150 }} />
              ) : !theme.sizes.isMobile ? (
                idea?.isSubmitted ? (
                  <Popup
                    trigger={
                      <StatusLabel size="tiny" color="green">
                        {t("generic.submitted")}
                      </StatusLabel>
                    }
                    content={`Submitted ${formatDate(idea?.submittedAt)}`}
                  />
                ) : (
                  <StatusLabel size="tiny" color="yellow">
                    <Icon name="file outline" /> {t("generic.draft")}
                  </StatusLabel>
                )
              ) : null}
              {canShowHelpModal && !theme.sizes.isMobile ? (
                <Popup
                  header={t("challenges.viewDetails")}
                  trigger={
                    <Button
                      size="mini"
                      icon="eye"
                      onClick={() => setHelpModalOpen(true)}
                      inverted
                      basic
                    />
                  }
                />
              ) : null}
              {idea?.ownerChallenge ? (
                <Button
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 250,
                  }}
                  content={`${idea?.ownerChallenge?.name}`}
                  size="mini"
                  as={Link}
                  to={`/challenges/${idea?.challenge}?noRedirect`}
                  inverted
                  basic
                />
              ) : null}
              {!theme.sizes.isMobile && <IdeaFollowButton />}
            </StatusbarInnerContainer>

            <StatusbarInnerContainer $flexGrow={0}>
              {isEditing && !websocketConnected && !theme.sizes.isMobile ? (
                <Popup
                  header="Connection issues"
                  content={`Collaboration features are disabled. Your ${t("generic.ideas")} will still be saved automatically.`}
                  trigger={
                    <Icon color="yellow" size="large" name="warning sign" />
                  }
                />
              ) : null}

              {!theme.sizes.isMobile ? (
                <div style={{ display: "flex", alignItems: "center" }}>
                  {otherUsersValues.length ? (
                    <Popup
                      header="Collaborators online"
                      content={otherUsersValues
                        .map((u) => u.profile?.fullName)
                        .join(", ")}
                      trigger={
                        <div>
                          {otherUsersValues.map((u, index) => (
                            <ImageWithFallback
                              key={u._id}
                              avatar
                              src={util.avatarUrl(u)}
                              fallbackSrc={util.avatarUrl()}
                              style={{
                                marginLeft: index === 0 ? 0 : -8,
                                minWidth: 25,
                              }}
                            />
                          ))}
                        </div>
                      }
                    />
                  ) : null}
                </div>
              ) : null}

              {loading ? (
                <PlaceholderItem height={25} style={{ width: 150 }} />
              ) : (util.canEditIdea(user, idea) ||
                  util.canManageChallenge(user, idea?.ownerChallenge) ||
                  util.hasPermission(
                    user,
                    "challenge.updateIdeaStatus",
                    idea?.challenge,
                  )) &&
                !idea.children ? (
                <>
                  {!theme.sizes.isMobile ? (
                    idea?.isSubmitted &&
                    (util.hasPermission(
                      user,
                      "challenge.editIdeas",
                      idea?.challenge,
                    ) ||
                      util.hasPermission(
                        user,
                        "org.manageChallenges",
                        idea?.ownerChallenge?.organisation,
                      )) ? (
                      <StyledButton
                        size="tiny"
                        content={
                          isEditing
                            ? "Done editing"
                            : `Edit ${t("generic.idea")}`
                        }
                        onClick={() => {
                          setEditing(!isEditing);
                        }}
                        icon={isEditing ? "check" : "edit"}
                        secondary
                      />
                    ) : !idea?.isSubmitted && util.canEditIdea(user, idea) ? (
                      <StyledButton
                        size="tiny"
                        content={t(
                          isEditing
                            ? "ideas.header.preview"
                            : "ideas.header.edit",
                        )}
                        onClick={() => {
                          setEditing(!isEditing);
                          if (isEditing) {
                            api.journey.record(
                              "ideas",
                              idea?._id,
                              "previewedIdea",
                            );
                          } else {
                            api.journey.record(
                              "ideas",
                              idea?._id,
                              "resumedEditingIdea",
                            );
                          }
                        }}
                        icon="edit"
                        secondary
                      />
                    ) : null
                  ) : null}

                  <SubmitIdeaButton
                    updateIdea={updateIdea}
                    onSubmit={onSubmit}
                    isSaving={isSaving}
                    unsaved={unsaved}
                    isStatusBar={true}
                  />
                </>
              ) : null}
            </StatusbarInnerContainer>
          </StatusbarContainer>
        </StatusbarOuterContainer>
      </IdeaStatusBarDiv>
    </Sticky>
  );
};

export default IdeaStatusBar;
